import { Eye, Pencil } from "lucide-react";
import { Link } from "react-router-dom";
import { memo } from "react";
import type { CSSProperties, ReactNode } from "react";

import { GuardedActionLink } from "@/components/auth";
import type { DeviceDto } from "@/lib/api/types";
import {
  deviceActivityModelFromApi,
  DeviceOperationalStatus,
  formatDeviceOperationalStatusLabel,
} from "@/lib/devices/device-state-model";
import {
  formatDeviceLastSeen,
  getDeviceAssignedUsers,
  getDeviceLifecycleDates,
  getDeviceLocationLabel,
  getDevicePhotoPath,
  getDeviceProtocolLabel,
  getDeviceSamplingLabel,
  getDeviceSecondaryIdentifier,
  getDeviceTags,
} from "@/lib/devices/device-field-helpers";
import { cn } from "@/lib/utils/cn";

import { DeviceActionsDropdown } from "@/modules/devices/components/device-actions-dropdown";
import { PrivateDeviceImage } from "@/modules/devices/components/private-device-image";

/** Matches `DeviceTable` header grid — one value per column. */
export const DEVICE_TABLE_ROW_GRID =
  "grid grid-cols-[2rem_minmax(132px,1fr)_8rem_7rem_6.5rem_8rem_6rem_6.5rem_5.75rem_6.75rem_6.75rem_6.75rem_minmax(7rem,0.9fr)_7rem_7.5rem_6.5rem_minmax(9rem,auto)] gap-x-2.5 px-ds-3 py-ds-2.5 items-center text-caption border-b border-[color:var(--table-border)]";

export type DeviceManagementTableRowProps = {
  device: DeviceDto;
  rowIndex?: number;
  selected: boolean;
  onToggleSelected: (id: string) => void;
  onExternalDataAccess?: (d: DeviceDto) => void;
  onAssignUsers?: (d: DeviceDto) => void;
  onBackup?: (d: DeviceDto) => void;
  onBackupDelete?: (d: DeviceDto) => void;
  onDelete?: (d: DeviceDto) => void;
  onDisable?: (d: DeviceDto) => void;
  style?: CSSProperties;
};

function IconLink({
  to,
  label,
  children,
}: {
  to: string;
  label: string;
  children: ReactNode;
}) {
  return (
    <Link
      to={to}
      className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-card text-muted shadow-sm transition-colors hover:border-primary/20 hover:bg-muted-bg hover:text-foreground"
      title={label}
      aria-label={label}
    >
      {children}
    </Link>
  );
}

function TableCell({
  children,
  title,
  className,
}: {
  children: ReactNode;
  title?: string;
  className?: string;
}) {
  return (
    <div role="cell" title={title} className={cn("min-w-0 truncate text-caption font-medium text-foreground", className)}>
      {children}
    </div>
  );
}

function lifecycleDateValue(device: DeviceDto, label: string): string {
  const item = getDeviceLifecycleDates(device).find((entry) => entry.label === label);
  return item?.value ?? "—";
}

function statusChipClass(status: DeviceOperationalStatus): string {
  switch (status) {
    case DeviceOperationalStatus.ONLINE:
      return "bg-success/20 text-success";
    case DeviceOperationalStatus.OFFLINE:
      return "bg-status-offline-surface text-status-offline";
    case DeviceOperationalStatus.STALE:
      return "bg-warning/20 text-warning";
    case DeviceOperationalStatus.ERROR:
      return "bg-error/20 text-error";
    case DeviceOperationalStatus.MAINTENANCE:
      return "bg-primary/15 text-primary";
    case DeviceOperationalStatus.VISUALIZATION_DISABLED:
      return "bg-status-warning-surface text-status-warning";
    case DeviceOperationalStatus.INGESTION_DISABLED:
      return "bg-error/20 text-error";
    default:
      return "bg-muted-bg text-muted";
  }
}

export const DeviceManagementTableRow = memo(function DeviceManagementTableRow({
  device,
  rowIndex = 0,
  selected,
  onToggleSelected,
  onExternalDataAccess,
  onAssignUsers,
  onBackup,
  onBackupDelete,
  onDelete,
  onDisable,
  style,
}: DeviceManagementTableRowProps) {
  const model = deviceActivityModelFromApi(device);

  const protocol = getDeviceProtocolLabel(device);
  const sampling = getDeviceSamplingLabel(device);
  const location = getDeviceLocationLabel(device);
  const assignedUsers = getDeviceAssignedUsers(device);
  const tags = getDeviceTags(device);
  const uid = getDeviceSecondaryIdentifier(device);
  const devicePhotoPath = getDevicePhotoPath(device);
  const displayName = device.name?.trim() || device.device_uid;
  const lastSeen = formatDeviceLastSeen(device.activity_last_seen ?? device.last_seen);
  const statusLabel = formatDeviceOperationalStatusLabel(model.status);

  const primaryAssignee = assignedUsers.length > 0 ? assignedUsers[0]! : "Unassigned";
  const extraAssignees = assignedUsers.length > 1 ? `+${assignedUsers.length - 1} more` : "—";
  const assigneesTitle = assignedUsers.length > 0 ? assignedUsers.join(", ") : undefined;
  const tagsLabel = tags.length > 0 ? tags.join(", ") : "—";

  const healthTitle =
    model.expectedSamplesPerSec != null ? `Expected ${model.expectedSamplesPerSec} Hz` : undefined;

  const highlight =
    model.status === DeviceOperationalStatus.STALE || model.status === DeviceOperationalStatus.ERROR;

  return (
    <div
      style={style}
      className={cn(
        DEVICE_TABLE_ROW_GRID,
        "table-grid-row table-data-row min-h-[52px] bg-card transition-colors hover:bg-[var(--table-row-hover)]",
        rowIndex % 2 === 1 && "table-row-striped",
        selected && "!bg-primary/10",
        highlight && "!bg-warning/[0.06]",
      )}
      role="row"
    >
      <div className="flex justify-center" role="cell">
        <input
          type="checkbox"
          className="h-4 w-4 rounded border-border text-primary focus:ring-primary/20"
          checked={selected}
          onChange={() => onToggleSelected(device.id)}
          aria-label={`Select ${device.name ?? device.device_uid}`}
        />
      </div>

      <TableCell>
        <Link
          to={`/devices/${device.id}`}
          className="flex min-w-0 items-center gap-2 font-medium text-foreground hover:text-link hover:underline"
          title={displayName}
        >
          {devicePhotoPath ? (
            <PrivateDeviceImage
              photoPath={devicePhotoPath}
              alt={displayName}
              silent
              className="h-9 w-9 shrink-0 rounded-lg border border-border/60 bg-muted-bg/30 object-cover"
            />
          ) : null}
          <span className="min-w-0 truncate">{displayName}</span>
        </Link>
      </TableCell>

      <TableCell title={uid}>
        {uid}
      </TableCell>

      <TableCell title={tags.length > 0 ? tagsLabel : undefined}>
        {tagsLabel}
      </TableCell>

      <div role="cell">
        <span
          title={healthTitle}
          className={cn(
            "inline-flex max-w-full items-center truncate rounded-full px-2.5 py-1 text-micro font-bold uppercase tracking-wide",
            statusChipClass(model.status),
          )}
        >
          {statusLabel}
        </span>
      </div>

      <TableCell className="tabular-nums" title={lastSeen}>
        {lastSeen}
      </TableCell>

      <TableCell className="text-foreground" title={protocol}>
        {protocol}
      </TableCell>

      <TableCell className="text-foreground" title={sampling.primary}>
        {sampling.primary}
      </TableCell>

      <TableCell title={sampling.secondary}>
        {sampling.secondary}
      </TableCell>

      <TableCell>{lifecycleDateValue(device, "Install")}</TableCell>

      <TableCell>{lifecycleDateValue(device, "Warranty")}</TableCell>

      <TableCell>{lifecycleDateValue(device, "Calibration")}</TableCell>

      <TableCell className="text-foreground" title={location.title}>
        {location.primary}
      </TableCell>

      <TableCell title={location.secondary || undefined}>
        {location.secondary || "—"}
      </TableCell>

      <TableCell className="text-foreground" title={assigneesTitle}>
        {primaryAssignee}
      </TableCell>

      <TableCell title={assignedUsers.length > 1 ? assigneesTitle : undefined}>
        {extraAssignees}
      </TableCell>

      <div className="flex items-center justify-end gap-ds-2" role="cell">
        <IconLink to={`/devices/${device.id}`} label="View logger">
          <Eye className="h-4 w-4" aria-hidden />
        </IconLink>
        <GuardedActionLink
          to={`/devices/${device.id}/edit`}
          moduleKey="devices"
          action="edit"
          deniedMessage="You don't have permission to edit data loggers."
          className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-card text-muted shadow-sm transition-colors hover:border-primary/20 hover:bg-muted-bg hover:text-foreground"
          title="Edit logger"
          aria-label="Edit logger"
        >
          <Pencil className="h-4 w-4" aria-hidden />
        </GuardedActionLink>
        <DeviceActionsDropdown
          device={device}
          onExternalDataAccess={onExternalDataAccess}
          onAssignUsers={onAssignUsers}
          onBackup={onBackup}
          onBackupDelete={onBackupDelete}
          onDelete={onDelete}
          onDisable={onDisable}
        />
      </div>
    </div>
  );
});
